import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mail, Check, X, AlertCircle, Building2, CreditCard, Clock,
  Search, Filter, Loader2, ChevronDown, ChevronUp, Eye
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminEmailApprovalPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [approvalRequests, setApprovalRequests] = useState([]);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [searchTerm, setSearchTerm] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [expandedCompany, setExpandedCompany] = useState(null);
  
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all companies
      const companiesRes = await axios.get(`${API}/api/admin/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(companiesRes.data.companies || []);
      
      // Fetch approval requests
      try {
        const requestsRes = await axios.get(`${API}/api/admin/email-approval-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setApprovalRequests(requestsRes.data.requests || []);
      } catch (e) {
        console.log('No approval requests endpoint or no requests');
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      if (err.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (companyId, approve, monthlyLimit = 1000) => {
    setProcessingId(companyId);
    try {
      await axios.put(
        `${API}/api/admin/companies/${companyId}/email-approval`,
        {
          email_approved: approve,
          email_monthly_limit: monthlyLimit,
          reason: approve ? 'Approved by admin' : 'Rejected by admin'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(approve ? 'Company approved for email sending' : 'Company email permission revoked');
      fetchData();
    } catch (err) {
      toast.error('Failed to update approval status');
    } finally {
      setProcessingId(null);
    }
  };

  const filteredCompanies = companies.filter(company => {
    // Apply search filter
    const matchesSearch = company.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         company.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Apply status filter
    if (filter === 'approved') return matchesSearch && company.email_approved === true;
    if (filter === 'pending') return matchesSearch && !company.email_approved;
    if (filter === 'rejected') return matchesSearch && company.email_approved === false;
    
    return matchesSearch;
  });

  const getApprovalStatus = (company) => {
    if (company.email_approved === true) return { status: 'approved', color: 'green', label: 'Approved' };
    if (company.email_approved === false) return { status: 'rejected', color: 'red', label: 'Rejected' };
    return { status: 'pending', color: 'yellow', label: 'Pending' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="animate-spin text-indigo-600" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="text-indigo-600" size={28} />
            Email Approval Management
          </h1>
          <p className="text-gray-500 text-sm mt-1">Approve or revoke company email sending permissions</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-3xl font-bold text-gray-900">{companies.length}</p>
            <p className="text-sm text-gray-500">Total Companies</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-3xl font-bold text-green-600">
              {companies.filter(c => c.email_approved === true).length}
            </p>
            <p className="text-sm text-gray-500">Approved</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-3xl font-bold text-yellow-600">
              {companies.filter(c => !c.email_approved && c.email_approved !== false).length}
            </p>
            <p className="text-sm text-gray-500">Pending Review</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-3xl font-bold text-red-600">
              {companies.filter(c => c.email_approved === false).length}
            </p>
            <p className="text-sm text-gray-500">Rejected</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Filter size={18} className="text-gray-400" />
              {['all', 'pending', 'approved', 'rejected'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize ${
                    filter === f 
                      ? 'bg-indigo-100 text-indigo-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Companies List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Company</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Credits</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Monthly Limit</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredCompanies.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No companies found matching your criteria
                  </td>
                </tr>
              ) : (
                filteredCompanies.map(company => {
                  const approval = getApprovalStatus(company);
                  const isExpanded = expandedCompany === company.id;
                  
                  return (
                    <React.Fragment key={company.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                              <Building2 size={20} className="text-indigo-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{company.company_name || 'Unnamed'}</p>
                              <p className="text-xs text-gray-500">ID: {company.id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{company.email}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1 text-sm">
                            <CreditCard size={14} className="text-gray-400" />
                            <span>{company.email_credits?.available_credits || 0}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
                            ${approval.color === 'green' ? 'bg-green-100 text-green-700' : ''}
                            ${approval.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' : ''}
                            ${approval.color === 'red' ? 'bg-red-100 text-red-700' : ''}
                          `}>
                            {approval.status === 'approved' && <Check size={12} />}
                            {approval.status === 'rejected' && <X size={12} />}
                            {approval.status === 'pending' && <Clock size={12} />}
                            {approval.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {company.email_monthly_limit || '-'}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {approval.status !== 'approved' && (
                              <button
                                onClick={() => handleApproval(company.id, true)}
                                disabled={processingId === company.id}
                                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white rounded-lg text-sm font-medium flex items-center gap-1"
                              >
                                {processingId === company.id ? (
                                  <Loader2 className="animate-spin" size={14} />
                                ) : (
                                  <Check size={14} />
                                )}
                                Approve
                              </button>
                            )}
                            {approval.status === 'approved' && (
                              <button
                                onClick={() => handleApproval(company.id, false)}
                                disabled={processingId === company.id}
                                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white rounded-lg text-sm font-medium flex items-center gap-1"
                              >
                                {processingId === company.id ? (
                                  <Loader2 className="animate-spin" size={14} />
                                ) : (
                                  <X size={14} />
                                )}
                                Revoke
                              </button>
                            )}
                            <button
                              onClick={() => setExpandedCompany(isExpanded ? null : company.id)}
                              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                            >
                              {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                            </button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded Details */}
                      {isExpanded && (
                        <tr className="bg-gray-50">
                          <td colSpan="6" className="px-6 py-4">
                            <div className="grid grid-cols-3 gap-4">
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Company Details</p>
                                <p className="text-sm text-gray-900">ID: {company.id}</p>
                                <p className="text-sm text-gray-600">Created: {company.created_at ? new Date(company.created_at).toLocaleDateString() : 'N/A'}</p>
                                <p className="text-sm text-gray-600">Active: {company.is_active ? 'Yes' : 'No'}</p>
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Email Usage</p>
                                <p className="text-sm text-gray-900">Available: {company.email_credits?.available_credits || 0}</p>
                                <p className="text-sm text-gray-600">Purchased: {company.email_credits?.total_purchased || 0}</p>
                                <p className="text-sm text-gray-600">Used: {company.email_credits?.total_used || 0}</p>
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Approval Info</p>
                                <p className="text-sm text-gray-900">Status: {approval.label}</p>
                                <p className="text-sm text-gray-600">Reason: {company.email_approval_reason || 'N/A'}</p>
                                <p className="text-sm text-gray-600">
                                  Updated: {company.email_approval_updated_at 
                                    ? new Date(company.email_approval_updated_at).toLocaleString() 
                                    : 'Never'}
                                </p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pending Approval Requests */}
        {approvalRequests.length > 0 && (
          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-xl p-5">
            <h3 className="font-semibold text-yellow-800 flex items-center gap-2 mb-4">
              <AlertCircle size={18} />
              Pending Approval Requests ({approvalRequests.length})
            </h3>
            <div className="space-y-3">
              {approvalRequests.map(request => (
                <div key={request.id} className="bg-white rounded-lg p-4 border border-yellow-200 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{request.company_name}</p>
                    <p className="text-sm text-gray-600">{request.company_email}</p>
                    <p className="text-xs text-gray-500 mt-1">Reason: {request.reason}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleApproval(request.company_id, true)}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleApproval(request.company_id, false)}
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEmailApprovalPage;
